import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Wallet, Building2, User, Calculator, AlertTriangle, Pencil, Trash2, X, Check, ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Cell } from 'recharts';
import { usePharmacies } from '../context/PharmacyContext';
import ExpenseDateRangePicker from '../components/ExpenseDateRangePicker';
import ExpensePharmacyFilter from '../components/ExpensePharmacyFilter';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { FilterableHeader, useTableFilters } from '../components/TableFilters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function Tresorerie({ hideHeader = false, dateRange: externalDateRange }) {
  const { getPharmacyIdsParam } = usePharmacies();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [currentUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  });
  
  const [dateRange, setDateRange] = useState(externalDateRange || {
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });

  useEffect(() => {
    if (externalDateRange) {
      setDateRange(externalDateRange);
    }
  }, [externalDateRange]);
  
  const [superAdminPharmacyFilter, setSuperAdminPharmacyFilter] = useState('all');
  const [chartPeriod, setChartPeriod] = useState('Jour');
  const [cashOverviewPeriod, setCashOverviewPeriod] = useState('Semaine');

  // Edit modal state
  const [editingFlux, setEditingFlux] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, [dateRange, superAdminPharmacyFilter]);

  const fetchCategories = async () => {
    try {
      const headers = { 'X-User-Id': currentUser?.id };
      const res = await axios.get(`${API}/categories`, { headers });
      setCategories((res.data || []).filter(c => c.type === 'expense'));
    } catch (e) {
      console.error('Erreur chargement catégories', e);
    }
  };

  const handleEdit = (flux) => {
    setEditingFlux(flux);
    setEditForm({
      category_id: flux.category_id || '',
      subcategory: flux.subcategory || '',
      supplier: flux.supplier || '',
      payment_method: flux.mode === 'Espèces' ? 'cash' : (flux.mode || 'cash'),
      amount: Math.abs(flux.montant),
    });
  };

  const handleSaveEdit = async () => {
    if (!editingFlux) return;
    setEditLoading(true);
    try {
      const headers = { 'X-User-Id': currentUser?.id };

      const body = {
        date: editingFlux.date || new Date().toISOString(),
        amount: parseFloat(editForm.amount),
        category_id: editForm.category_id || editingFlux.category_id || '',
        subcategory: editForm.subcategory || null,
        description: editingFlux.description || '',
        payment_method: editForm.payment_method,
        supplier: editForm.supplier || null,
        invoice_number: editingFlux.invoice_number || null,
        invoice_image_url: editingFlux.invoice_image_url || null,
      };

      await axios.put(`${API}/expenses/${editingFlux.id}`, body, { headers });
      toast.success('Dépense mise à jour avec succès!');
      setEditingFlux(null);
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (flux) => {
    try {
      const headers = { 'X-User-Id': currentUser?.id };
      await axios.delete(`${API}/expenses/${flux.id}`, { headers });
      toast.success('Dépense supprimée avec succès!');
      setDeleteConfirm(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const fetchData = async () => {
    if (!currentUser?.id) {
      toast.error('Session invalide');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const startDate = dateRange?.from || (dateRange?.from === null ? new Date(2000, 0, 1) : startOfMonth(new Date()));
      const endDate = dateRange?.to || (dateRange?.to === null ? new Date(2100, 11, 31) : endOfMonth(new Date()));

      const headers = { 'X-User-Id': currentUser.id };

      let pharmacyIds;
      if (currentUser?.role === 'super_admin') {
        pharmacyIds = superAdminPharmacyFilter === 'all' ? 'all' : superAdminPharmacyFilter;
      } else {
        pharmacyIds = getPharmacyIdsParam();
      }

      const response = await axios.get(`${API}/tresorerie`, {
        headers,
        params: {
          start_date: format(startDate, "yyyy-MM-dd'T'00:00:00.000'Z'"),
          end_date: format(endDate, "yyyy-MM-dd'T'23:59:59.999'Z'"),
          pharmacy_ids: pharmacyIds
        }
      });

      setData(response.data);
    } catch (error) {
      console.error('Error fetching tresorerie data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatChartData = () => {
    if (!data?.daily_data) return [];
    
    // Sort by date chronologically
    return Object.entries(data.daily_data)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))  // Tri chronologique
      .map(([date, values]) => {
        // Parse date without timezone shift
        const dateObj = new Date(date);
        const localDate = new Date(dateObj.getTime() + dateObj.getTimezoneOffset() * 60000);
        
        return {
          date: localDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
          entrees: values.entrees,
          sorties: values.sorties,
          solde: values.solde
        };
      });
  };

  const formatWeeklyData = () => {
    if (!data?.daily_data) return [];
    
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const colors = ['#fdba74', '#fdba74', '#bef264', '#5eead4', '#5eead4', '#c084fc'];
    
    const weeklyData = weekDays.map((day, i) => ({
      day,
      total: 0,
      fill: colors[i]
    }));

    Object.entries(data.daily_data).forEach(([date, values]) => {
      const dayIndex = new Date(date).getDay();
      const adjustedIndex = dayIndex === 0 ? 5 : dayIndex - 1; // Map to Mon-Sat (0-5)
      if (adjustedIndex >= 0 && adjustedIndex < 6) {
        weeklyData[adjustedIndex].total += values.solde;
      }
    });

    return weeklyData;
  };

  const [tableFilteredFlux, setTableFilteredFlux] = useState(null);

  const fluxFilterConfig = [
    { key: 'type', accessor: (r) => r.type === 'revenue' ? 'Entrée' : 'Sortie' },
    { key: 'pharmacy_name', accessor: (r) => r.pharmacy_name || '—' },
    { key: 'category', accessor: (r) => r.parent_category || r.category || '—' },
    { key: 'payment_method', accessor: (r) => r.payment_method || '—' },
  ];

  const fluxCaisse = data?.flux_caisse || [];

  const [tresSortConfig, setTresSortConfig] = useState({ key: 'date', direction: 'desc' });
  const handleTresSort = (key) => {
    setTresSortConfig(current => {
      if (current.key === key) {
        return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const sortedFluxCaisse = useMemo(() => {
    let sortableItems = [...fluxCaisse];
    if (tresSortConfig !== null) {
      sortableItems.sort((a, b) => {
        let valA = 0, valB = 0;
        if (tresSortConfig.key === 'date') {
          valA = a.date ? parseSafeDate(a.date).getTime() : 0;
          valB = b.date ? parseSafeDate(b.date).getTime() : 0;
        } else if (tresSortConfig.key === 'pharmacie') {
          valA = (a.pharmacy_name || '').toLowerCase();
          valB = (b.pharmacy_name || '').toLowerCase();
        } else if (tresSortConfig.key === 'type') {
          valA = (a.type || '').toLowerCase();
          valB = (b.type || '').toLowerCase();
        } else if (tresSortConfig.key === 'utilisateur') {
          valA = (a.user_fullname || a.username || '').toLowerCase();
          valB = (b.user_fullname || b.username || '').toLowerCase();
        } else if (tresSortConfig.key === 'categorie') {
          valA = (a.category_name || a.category || a.parent_category || '').toLowerCase();
          valB = (b.category_name || b.category || b.parent_category || '').toLowerCase();
        } else if (tresSortConfig.key === 'sous_categorie') {
          valA = (a.subcategory || '').toLowerCase();
          valB = (b.subcategory || '').toLowerCase();
        } else if (tresSortConfig.key === 'fournisseur') {
          valA = (a.supplier || '').toLowerCase();
          valB = (b.supplier || '').toLowerCase();
        } else if (tresSortConfig.key === 'mode') {
          valA = (a.payment_method || '').toLowerCase();
          valB = (b.payment_method || '').toLowerCase();
        } else if (tresSortConfig.key === 'montant') {
          valA = a.amount || 0;
          valB = b.amount || 0;
        }

        if (valA < valB) return tresSortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return tresSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [fluxCaisse, tresSortConfig]);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">
      <div className="text-lg">Chargement...</div>
    </div>;
  }

  const kpis = data?.kpis || {};

  const SortableTresHeader = ({ label, sortKey, className = "" }) => {
    const isSorted = tresSortConfig.key === sortKey;
    return (
      <TableHead
        onClick={() => handleTresSort(sortKey)}
        className={`text-xs font-semibold text-gray-600 uppercase cursor-pointer select-none hover:text-gray-900 transition-colors ${className}`}
      >
        <div className={`inline-flex items-center gap-1 ${className.includes('text-right') ? 'justify-end w-full' : ''}`}>
          <span>{label}</span>
          {isSorted ? (
            tresSortConfig.direction === 'desc' ? (
              <ArrowDown className="w-3.5 h-3.5 text-blue-600 font-bold" />
            ) : (
              <ArrowUp className="w-3.5 h-3.5 text-blue-600 font-bold" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-gray-300 hover:text-gray-500" />
          )}
        </div>
      </TableHead>
    );
  };

  return (
    <div className={hideHeader ? "space-y-4 sm:space-y-6 fade-in" : "p-3 sm:p-6 space-y-4 sm:space-y-6 bg-gray-50 min-h-screen"}>
      {/* Header */}
      {!hideHeader && (
        <div className="mb-4 sm:mb-6">
          <div className="flex items-start justify-between mb-4 sm:mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Trésorerie</h1>
              <p className="text-gray-500 mt-1 text-sm sm:text-base">Analyse globale des flux et équilibre financier</p>
            </div>
          </div>

          {/* GRAND BLOC BLANC - Même design que Revenus/Dépenses */}
          <div className="bg-white rounded-3xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1 min-w-0">
                {currentUser?.role === 'super_admin' && (
                  <ExpensePharmacyFilter
                    selectedPharmacies={Array.isArray(superAdminPharmacyFilter) ? superAdminPharmacyFilter : []}
                    onChange={(ids) => setSuperAdminPharmacyFilter(ids.length === 0 ? 'all' : ids.join(','))}
                    currentUser={currentUser}
                  />
                )}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <ExpenseDateRangePicker
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3 sm:gap-4">
        {/* Entrées Cash */}
        <Card className="stat-card border-l-4 border-l-emerald-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <CardTitle className="text-sm font-medium text-gray-600">Entrées Cash</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{formatCurrency(kpis.entrees_caisse)} DA</div>
            <p className="text-xs text-emerald-600 mt-1">+25,9% vs S-1</p>
            <div className="mt-2 h-8">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={[{v:20},{v:25},{v:22},{v:30},{v:28}]}>
                  <Line type="monotone" dataKey="v" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Sorties Cash */}
        <Card className="stat-card border-l-4 border-l-orange-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-orange-600" />
              </div>
              <CardTitle className="text-sm font-medium text-gray-600">Sorties Cash</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{formatCurrency(kpis.sorties_caisse)} DA</div>
            <p className="text-xs text-orange-600 mt-1">-8,2% vs S-1</p>
            <div className="mt-2 h-8">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={[{v:30},{v:25},{v:28},{v:20},{v:22}]}>
                  <Line type="monotone" dataKey="v" stroke="#f97316" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Solde théorique */}
        <Card className="stat-card border-l-4 border-l-blue-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-blue-600" />
              </div>
              <CardTitle className="text-sm font-medium text-gray-600">Solde théorique</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{formatCurrency((kpis.entrees_caisse || 0) - (kpis.sorties_caisse || 0))} DA</div>
            <div className="mt-2 h-8">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={[{v:15},{v:20},{v:18},{v:25},{v:23}]}>
                  <Line type="monotone" dataKey="v" stroke="#3b82f6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Total en Banque */}
        <Card className="stat-card border-l-4 border-l-purple-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <CardTitle className="text-sm font-medium text-gray-600">Solde réel</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{formatCurrency((kpis.solde_actuel || 0) - (kpis.ecart || 0))} DA</div>
            <div className="mt-2 h-8">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={[{v:25},{v:28},{v:30},{v:32},{v:35}]}>
                  <Line type="monotone" dataKey="v" stroke="#a855f7" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Caissier Principal */}
        <Card className="stat-card border-l-4 border-l-pink-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center">
                <User className="w-5 h-5 text-pink-600" />
              </div>
              <CardTitle className="text-sm font-medium text-gray-600">Ecart</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {kpis.principal_caissier ? (
              <>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`text-lg font-bold ${
                    (kpis.ecart || 0) === 0 ? 'text-gray-600' : (kpis.ecart || 0) > 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {-(kpis.ecart || 0) > 0 ? '+' : ''}{-(kpis.ecart || 0) < 0 ? '-' : ''}{formatCurrency(Math.abs(kpis.ecart || 0))}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 mt-2">Aucune donnée</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Flux de trésorerie */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Flux de trésorerie</h3>
              <div className="flex gap-2">
                <button
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    chartPeriod === 'Jour' 
                      ? 'bg-white text-gray-700 border border-gray-200' 
                      : 'bg-teal-600 text-white'
                  }`}
                  onClick={() => setChartPeriod('Jour')}
                >
                  Jour
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    chartPeriod === 'Semaine' 
                      ? 'bg-teal-600 text-white' 
                      : 'bg-white text-gray-700 border border-gray-200'
                  }`}
                  onClick={() => setChartPeriod('Semaine')}
                >
                  Semaine
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={formatChartData()}>
                <defs>
                  <pattern id="stripesGreen" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                  </pattern>
                  <pattern id="stripesRed" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                  </pattern>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#475569', 
                    border: 'none', 
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    padding: '12px'
                  }}
                  labelStyle={{ color: 'white', fontWeight: 'bold' }}
                  formatter={(value) => [`${formatCurrency(value)} DA`, '']}
                />
                <Bar 
                  dataKey="entrees" 
                  fill="#6ee7b7"
                  radius={[20, 20, 0, 0]}
                  style={{ filter: 'url(#stripesGreen)' }}
                />
                <Bar 
                  dataKey="sorties" 
                  fill="#fca5a5"
                  radius={[20, 20, 0, 0]}
                  style={{ filter: 'url(#stripesRed)' }}
                />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-1 bg-teal-400 rounded"></div>
                <span className="text-sm text-gray-600">Entrées</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-1 bg-red-400 rounded"></div>
                <span className="text-sm text-gray-600">Sorties</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Cash Overview */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Total Cash Overview</h3>
                <div className="flex gap-2">
                  <button
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      cashOverviewPeriod === 'Jour' 
                        ? 'bg-white text-gray-700 border border-gray-200' 
                        : 'bg-teal-600 text-white'
                    }`}
                    onClick={() => setCashOverviewPeriod('Jour')}
                  >
                    Jour
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      cashOverviewPeriod === 'Semaine' 
                        ? 'bg-teal-600 text-white' 
                        : 'bg-white text-gray-700 border border-gray-200'
                    }`}
                    onClick={() => setCashOverviewPeriod('Semaine')}
                  >
                    Semaine
                  </button>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={formatWeeklyData()}>
                  <defs>
                    <pattern id="stripesOrange" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                    </pattern>
                    <pattern id="stripesLightGreen" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                    </pattern>
                    <pattern id="stripesCashTeal" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                    </pattern>
                    <pattern id="stripesPurple" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                    </pattern>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#475569', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      padding: '12px'
                    }}
                    labelStyle={{ color: 'white', fontWeight: 'bold' }}
                    formatter={(value) => [`${formatCurrency(value)} DA`, '']}
                  />
                  <Bar 
                    dataKey="total" 
                    radius={[20, 20, 0, 0]}
                  >
                    {formatWeeklyData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <h3 className="text-lg font-semibold">Alertes</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Sorties cash élevées aujourd'hui</p>
                  <p className="text-2xl font-bold text-orange-600">{formatCurrency(kpis.sorties_caisse)} DA</p>
                  <p className="text-xs text-gray-600 mt-1">+30% vs sa moyenne journalière</p>
                </div>
                <Button size="sm" variant="link" className="text-orange-600 p-0">
                  Voir détails →
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Flux de caisse Table */}
      <Card>
        <CardContent className="p-3 sm:p-6">
          <h3 className="text-lg font-semibold mb-2">Flux de caisse</h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <SortableTresHeader label="Date" sortKey="date" />
                  <SortableTresHeader label="Pharmacie" sortKey="pharmacie" className="hidden sm:table-cell" />
                  <SortableTresHeader label="Type" sortKey="type" />
                  <SortableTresHeader label="Utilisateur" sortKey="utilisateur" />
                  <SortableTresHeader label="Catégorie" sortKey="categorie" className="hidden md:table-cell" />
                  <SortableTresHeader label="Sous-catégorie" sortKey="sous_categorie" className="hidden lg:table-cell" />
                  <SortableTresHeader label="Fournisseur" sortKey="fournisseur" className="hidden lg:table-cell" />
                  <SortableTresHeader label="Mode" sortKey="mode" className="hidden md:table-cell" />
                  <SortableTresHeader label="Montant" sortKey="montant" className="text-right" />
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedFluxCaisse.map((flux) => {
                  const fluxDate = parseSafeDate(flux.date);
                  const displayDate = new Date(fluxDate.getTime() + fluxDate.getTimezoneOffset() * 60000);
                  
                  // Couleur pharmacie
                  const pharmacyColors = [
                    { bg: 'bg-emerald-100', text: 'text-emerald-700' },
                    { bg: 'bg-blue-100', text: 'text-blue-700' },
                    { bg: 'bg-purple-100', text: 'text-purple-700' },
                    { bg: 'bg-amber-100', text: 'text-amber-700' },
                    { bg: 'bg-pink-100', text: 'text-pink-700' },
                  ];
                  const pName = flux.pharmacy_name || '';
                  let pHash = 0;
                  for (let i = 0; i < pName.length; i++) pHash = pName.charCodeAt(i) + ((pHash << 5) - pHash);
                  const pColor = pharmacyColors[Math.abs(pHash) % pharmacyColors.length];

                  // Couleur utilisateur
                  const userColors = [
                    { bg: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-500' },
                    { bg: 'bg-indigo-50', text: 'text-indigo-700', dot: 'bg-indigo-500' },
                    { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
                    { bg: 'bg-pink-50', text: 'text-pink-700', dot: 'bg-pink-500' },
                    { bg: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-500' },
                    { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-500' },
                  ];
                  const uName = flux.utilisateur || '';
                  let uHash = 0;
                  for (let i = 0; i < uName.length; i++) uHash = uName.charCodeAt(i) + ((uHash << 5) - uHash);
                  const uColor = userColors[Math.abs(uHash) % userColors.length];

                  // Catégorie parent (type)
                  const parentCat = flux.parent_category || flux.categorie || 'Autre';
                  const catBadge = parentCat === 'Achat' || parentCat.startsWith('Achat') 
                    ? 'bg-blue-100 text-blue-700' 
                    : parentCat === 'Charge' || parentCat.startsWith('Charge')
                    ? 'bg-red-100 text-red-700'
                    : parentCat === 'Salaire' || parentCat.startsWith('Salaire')
                    ? 'bg-orange-100 text-orange-700'
                    : parentCat === 'Clôture caisse'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-700';

                  const paymentLabels = { cash: 'Espèces', check: 'Chèque', transfer: 'Virement', card: 'Carte', Espèces: 'Espèces' };
                  
                  return (
                    <TableRow key={flux.id} className="hover:bg-gray-50">
                      <TableCell className="whitespace-nowrap text-sm font-medium text-gray-900">
                        {displayDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {pName ? (
                          <Badge className={`${pColor.bg} ${pColor.text} border-0 font-medium`}>
                            {pName}
                          </Badge>
                        ) : <span className="text-gray-400">-</span>}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={flux.type === 'Entrée' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}
                        >
                          {flux.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-full ${uColor.dot} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                            {uName.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-medium text-gray-800">{uName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge className={`${catBadge} border-0 font-medium text-xs`}>
                          {parentCat === 'Achat' ? 'Achats' : parentCat === 'Charge' ? 'Charges' : parentCat === 'Salaire' ? 'Salaires' : parentCat}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-700 hidden lg:table-cell">
                        {flux.category_name || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-gray-700 hidden lg:table-cell">
                        {flux.supplier || '-'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant="outline" className="text-xs">{paymentLabels[flux.mode] || flux.mode}</Badge>
                      </TableCell>
                      <TableCell className={`text-right font-semibold whitespace-nowrap ${flux.montant >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {flux.montant >= 0 ? '+' : ''}{formatCurrency(Math.abs(flux.montant))} DA
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {flux.type === 'Sortie' ? (
                            <>
                              <Button
                                size="sm" variant="ghost"
                                className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 h-8 w-8 p-0"
                                onClick={() => handleEdit(flux)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                                onClick={() => setDeleteConfirm(flux)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-300 pr-2">—</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={!!editingFlux} onOpenChange={(o) => !o && setEditingFlux(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier la dépense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Catégorie */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">Catégorie</Label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={editForm.category_id || ''}
                onChange={e => setEditForm(f => ({ ...f, category_id: e.target.value }))}
              >
                <option value="">-- Sélectionner --</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            {/* Sous-catégorie */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">Sous-catégorie</Label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Sous-catégorie (optionnel)"
                value={editForm.subcategory || ''}
                onChange={e => setEditForm(f => ({ ...f, subcategory: e.target.value }))}
              />
            </div>
            {/* Fournisseur */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">Fournisseur</Label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Nom du fournisseur (optionnel)"
                value={editForm.supplier || ''}
                onChange={e => setEditForm(f => ({ ...f, supplier: e.target.value }))}
              />
            </div>
            {/* Mode de paiement */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">Mode de paiement</Label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={editForm.payment_method || 'cash'}
                onChange={e => setEditForm(f => ({ ...f, payment_method: e.target.value }))}
              >
                <option value="cash">Espèces</option>
                <option value="check">Chèque</option>
                <option value="transfer">Virement</option>
                <option value="card">Carte</option>
                <option value="traite">Traite</option>
              </select>
            </div>
            {/* Montant */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">Montant (DA)</Label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={editForm.amount || ''}
                onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingFlux(null)} disabled={editLoading}>
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={editLoading}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              {editLoading ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Enregistrement...</>
              ) : (
                <><Check className="w-4 h-4 mr-1" /> Valider</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">
            Êtes-vous sûr de vouloir supprimer cette dépense de <strong>{formatCurrency(Math.abs(deleteConfirm?.montant || 0))} DA</strong> ? Cette action est irréversible.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm)}
            >
              <Trash2 className="w-4 h-4 mr-1" /> Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Tresorerie;
